import React from "react";
import classNames from "classnames";
import FormSection from "./FormSection";
import { OrderFormLocale } from "../types";
import styles from "./orderForm.module.scss";

export type SegmentedToggleLabels = {
  legend: string;
  firstOption: string;
  secondOption: string;
  firstDescription: string;
  secondDescription: string;
};

type SegmentedToggleProps = {
  locale?: OrderFormLocale;
  labels?: SegmentedToggleLabels;
  checked: boolean;
  disabled?: boolean;
  className?: string;
  onChange: (value: boolean) => void;
};

function segmentedLabelsFromLocale(locale: OrderFormLocale): SegmentedToggleLabels {
  const { schedule } = locale;
  return {
    legend: schedule.transferModeLabel,
    firstOption: schedule.transferModeTransfer,
    secondOption: schedule.transferModeRental,
    firstDescription: schedule.transferModeTransferDescription,
    secondDescription: schedule.transferModeRentalDescription,
  };
}

const SegmentedToggle: React.FC<SegmentedToggleProps> = ({
  locale,
  labels: labelsProp,
  checked,
  disabled = false,
  className,
  onChange,
}) => {
  const labels = labelsProp ?? (locale ? segmentedLabelsFromLocale(locale) : null);
  if (!labels) return null;

  return (
    <FormSection
      title={labels.legend}
      visuallyHiddenTitle
      className={classNames(styles.segmentedWrap, className, {
        [styles.segmentedWrapDisabled]: disabled,
      })}
    >
      <div
        className={classNames(styles.segmentedControl, {
          [styles.segmentedControlRight]: !checked,
        })}
        role="radiogroup"
        aria-label={labels.legend}
        aria-disabled={disabled || undefined}
      >
        <span className={styles.segmentedThumb} aria-hidden />

        <button
          type="button"
          className={classNames(styles.segmentedOption, {
            [styles.segmentedOptionSelected]: checked,
          })}
          aria-pressed={checked}
          disabled={disabled}
          onClick={() => onChange(true)}
        >
          {labels.firstOption}
        </button>

        <button
          type="button"
          className={classNames(styles.segmentedOption, {
            [styles.segmentedOptionSelected]: !checked,
          })}
          aria-pressed={!checked}
          disabled={disabled}
          onClick={() => onChange(false)}
        >
          {labels.secondOption}
        </button>
      </div>

      <p className={styles.segmentedDescription}>
        {checked ? labels.firstDescription : labels.secondDescription}
      </p>
    </FormSection>
  );
};

export default SegmentedToggle;
